import type { ProseChange, ProseSection } from '@libs/types';

/**
 * Where each deleted passage goes when it is put back into the proposed
 * document.
 *
 * `afterBlock` is keyed by anchor block id, `sectionStart` / `sectionEnd` by
 * section index.
 */
export interface DeletionPlan {
  afterBlock: Map<string, ProseChange[]>;
  sectionStart: Map<number, ProseChange[]>;
  sectionEnd: Map<number, ProseChange[]>;
}

function push<K>(map: Map<K, ProseChange[]>, key: K, change: ProseChange): void {
  const bucket = map.get(key);
  if (bucket) bucket.push(change);
  else map.set(key, [change]);
}

function sectionOfAnchor(sections: ProseSection[], anchorBlockId: string): number {
  return sections.findIndex((section) =>
    section.blocks.some((block) => block.blockId === anchorBlockId),
  );
}

/**
 * Place every deletion back into the AI's document for display.
 *
 * `generatedContent` is the all-changes-applied baseline, so a deleted block is
 * simply not in it — the reviewer has to be shown it anyway, both to judge the
 * deletion and, once it is rejected, to see the passage return.
 *
 * The rules mirror the finalize-time reconstruction in
 * `apps/api/.../rendering/remediation-reconstruct.util.ts` exactly, because this
 * pane is a preview of what that will produce:
 *
 * - anchor present  → directly after the anchor block, several deletions on one
 *                     anchor keeping their relative order;
 * - anchor null     → the start of the section whose `sectionKey` matches,
 *                     falling back to the first section;
 * - anchor missing  → the end of the first section, which is where the backend's
 *                     `insertAtAnchor` degrades to when it cannot find the id.
 *
 * The last two used to be dropped from the pane entirely: they were bucketed by
 * a key no block could match, so the passage never appeared and rejecting the
 * deletion looked like it had done nothing.
 */
export function planDeletions(sections: ProseSection[], changes: ProseChange[]): DeletionPlan {
  const plan: DeletionPlan = {
    afterBlock: new Map(),
    sectionStart: new Map(),
    sectionEnd: new Map(),
  };

  for (const change of changes) {
    if (change.type !== 'deletion') continue;

    if (change.anchorBlockId) {
      const owner = sectionOfAnchor(sections, change.anchorBlockId);
      if (owner >= 0) push(plan.afterBlock, change.anchorBlockId, change);
      else push(plan.sectionEnd, 0, change);
      continue;
    }

    const bySectionKey = sections.findIndex(
      (section) => (section.sectionKey ?? null) === (change.sectionKey ?? null),
    );
    push(plan.sectionStart, bySectionKey >= 0 ? bySectionKey : 0, change);
  }

  return plan;
}
